import type { ThemeKey } from './themes';
import type { SectionType } from './sectionTypes';

export interface VisualTemplate {
  id: string;
  name: string;
  description: string;
  theme_preset: ThemeKey;
  custom_accent?: string;
  custom_bg?: string;
  custom_card_bg?: string;
  custom_text?: string;
  /** Default sections to seed (with empty items). */
  sections: { name: string; type: SectionType }[];
  /** Default bio placeholder (user can replace). */
  suggestedBio?: string;
}

export const VISUAL_TEMPLATES: VisualTemplate[] = [
  {
    id: 'founder',
    name: 'The Founder',
    description: 'Quiet, confident, restrained. Bone background with moss accent.',
    theme_preset: 'bone',
    sections: [
      { name: 'Contact', type: 'contact' },
      { name: 'Socials', type: 'social' },
      { name: 'Press', type: 'links' },
    ],
    suggestedBio: 'Building something quiet. Talking to founders, operators, and the genuinely curious.',
  },
  {
    id: 'realtor',
    name: 'The Realtor',
    description: 'Warm sand palette. Listings, photo gallery, calendar booking.',
    theme_preset: 'sand',
    sections: [
      { name: 'Contact', type: 'contact' },
      { name: 'Featured listings', type: 'gallery' },
      { name: 'Book a showing', type: 'links' },
      { name: 'Reviews', type: 'faq' },
    ],
    suggestedBio: 'Helping good people find good homes. Boutique service across the city.',
  },
  {
    id: 'podcaster',
    name: 'The Podcaster',
    description: 'Ink-on-black with episode embeds front and center.',
    theme_preset: 'ink',
    sections: [
      { name: 'Latest episodes', type: 'videos' },
      { name: 'Listen', type: 'links' },
      { name: 'Connect', type: 'social' },
      { name: 'About the show', type: 'faq' },
    ],
    suggestedBio: 'Host of a show about the people who quietly shape industries.',
  },
  {
    id: 'consultant',
    name: 'The Consultant',
    description: 'Slate palette. Calendar booking, case studies, files.',
    theme_preset: 'slate',
    sections: [
      { name: 'Book a call', type: 'links' },
      { name: 'Case studies', type: 'files' },
      { name: 'Contact', type: 'contact' },
      { name: 'FAQ', type: 'faq' },
    ],
    suggestedBio: 'Strategy and operations for early-stage teams. Quiet, surgical, fast.',
  },
  {
    id: 'creative',
    name: 'The Creative',
    description: 'Custom palette ready to brand. Gallery-first layout.',
    theme_preset: 'custom',
    custom_bg: '#1A1A1F',
    custom_card_bg: '#26262C',
    custom_text: '#F2EFE8',
    custom_accent: '#D9CDB8',
    sections: [
      { name: 'Work', type: 'gallery' },
      { name: 'Videos', type: 'videos' },
      { name: 'Socials', type: 'social' },
      { name: 'Hire me', type: 'contact' },
    ],
    suggestedBio: 'Photographer & director. Considered work for considered brands.',
  },
  {
    id: 'maker',
    name: 'The Maker',
    description: 'Cream frame. A small shop with products, pickup FAQ, and contact.',
    theme_preset: 'cream',
    sections: [
      { name: 'Shop', type: 'products' },
      { name: 'Contact', type: 'contact' },
      { name: 'Socials', type: 'social' },
      { name: 'Ordering & pickup', type: 'faq' },
    ],
    suggestedBio: 'Small-batch goods, made by hand. Order here, pay with Venmo or Zelle, pick up Saturdays.',
  },
  {
    id: 'speaker',
    name: 'The Speaker',
    description: 'Moss palette. Past talks, book a session, downloads.',
    theme_preset: 'moss',
    sections: [
      { name: 'Talks', type: 'videos' },
      { name: 'Resources', type: 'files' },
      { name: 'Book me', type: 'contact' },
      { name: 'Socials', type: 'social' },
    ],
    suggestedBio: 'Keynote speaker on craft, focus, and the cost of attention.',
  },
];

export function templateById(id: string | undefined | null): VisualTemplate | undefined {
  return VISUAL_TEMPLATES.find((t) => t.id === id);
}
